/**
 * Tracking Parameters Utility
 * Handles utm_campaign, _ga and other tracking parameters
 * Stores in sessionStorage for reservation sourceId determination
 */

const HOTEL_ADS_KEY = 'hoteladds';
const CUSTOM_GA_KEY = 'custom_ga';

/**
 * Check URL for tracking parameters and store in sessionStorage
 * Should be called on page load in main components (DesktopComponent, AvailabilityComponent)
 * @param {URLSearchParams} searchParams - React Router searchParams
 */
export const captureTrackingParams = (searchParams) => {
  try {
    // Check for utm_campaign=HotelAds
    const utmCampaign = searchParams.get('utm_campaign');
    if (utmCampaign && utmCampaign.toLowerCase() === 'hotelads') {
      // Store timestamp like Laravel: session::put('hoteladds', 1677165989)
      const timestamp = Math.floor(Date.now() / 1000);
      sessionStorage.setItem(HOTEL_ADS_KEY, timestamp.toString());
      console.log('Tracking: HotelAds campaign detected, stored:', timestamp);
    }

    // Check for _ga parameter
    const gaParam = searchParams.get('_ga');
    if (gaParam) {
      sessionStorage.setItem(CUSTOM_GA_KEY, gaParam);
      // Also set cookie for persistence across sessions (like Laravel Cookie::queue)
      document.cookie = `custom_ga=${gaParam}; max-age=999999999; path=/`;
      console.log('Tracking: _ga parameter stored:', gaParam);
    }
  } catch (error) {
    console.error('Error capturing tracking params:', error);
  }
};

/**
 * Check if user came from Google Hotel Ads
 * @returns {boolean}
 */
export const isFromHotelAds = () => {
  try {
    const hotelAds = sessionStorage.getItem(HOTEL_ADS_KEY);
    return !!hotelAds;
  } catch (error) {
    return false;
  }
};

/**
 * Get the sourceId for reservation based on tracking params
 * If hoteladds session exists -> 'Google Hotel Ads'
 * Otherwise -> 'Direct'
 * @returns {string}
 */
export const getSourceId = () => {
  if (isFromHotelAds()) {
    return 'Google Hotel Ads';
  }
  return 'Direct';
};

/**
 * Get custom GA parameter if stored
 * @returns {string|null}
 */
export const getCustomGA = () => {
  try {
    // Try sessionStorage first
    const sessionGA = sessionStorage.getItem(CUSTOM_GA_KEY);
    if (sessionGA) {
      return sessionGA;
    }

    // Fallback to cookie
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'custom_ga') {
        return value;
      }
    }
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Clear tracking data (optional - for testing/logout)
 */
export const clearTrackingData = () => {
  try {
    sessionStorage.removeItem(HOTEL_ADS_KEY);
    sessionStorage.removeItem(CUSTOM_GA_KEY);
    document.cookie = 'custom_ga=; max-age=0; path=/';
  } catch (error) {
    console.error('Error clearing tracking data:', error);
  }
};

// ============================================
// MICROSOFT CLARITY INTEGRATION
// ============================================

const CLARITY_PROJECT_ID = 'vibzwvsomi'; // Clarity project ID

let clarityInitialized = false;

/**
 * Initialize Microsoft Clarity script
 * Should only be called on first/second page, NOT on booking-details
 */
export const initializeClarity = () => {
  if (clarityInitialized || typeof window === 'undefined') {
    return;
  }

  try {
    // Check if already initialized
    if (window.clarity) {
      clarityInitialized = true;
      return;
    }

    // Clarity initialization script
    (function(c, l, a, r, i, t, y) {
      c[a] = c[a] || function() { (c[a].q = c[a].q || []).push(arguments); };
      t = l.createElement(r);
      t.async = 1;
      t.src = "https://www.clarity.ms/tag/" + i;
      y = l.getElementsByTagName(r)[0];
      y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", CLARITY_PROJECT_ID);

    clarityInitialized = true;
    
  } catch (error) {
    console.error('Clarity: Failed to initialize:', error);
  }
};

/**
 * Set Clarity custom data
 * @param {string} key - Data key
 * @param {string} value - Data value
 */
export const setClarityData = (key, value) => {
  try {
    if (typeof window !== 'undefined' && window.clarity) {
      window.clarity("set", key, value);
     
    }
  } catch (error) {
    console.error('Clarity: Failed to set data:', error);
  }
};

/**
 * Initialize Clarity with accommodation data
 * Call this on DesktopComponent, AvailabilityComponent, UnifiedCheckout
 * @param {Object} options - Configuration options
 * @param {string} options.accommodationName - Hotel name
 * @param {string} options.accommodationId - Hotel ID
 * @param {string} options.checkIn - Check-in date (optional)
 * @param {string} options.checkOut - Check-out date (optional)
 * @param {number} options.adults - Number of adults (optional)
 * @param {number} options.children - Number of children (optional)
 * @param {boolean} options.isAvailable - Availability status (optional)
 */
export const initializeClarityWithData = (options = {}) => {
  const {
    accommodationName,
    accommodationId,
    checkIn,
    checkOut,
    adults,
    children,
    isAvailable
  } = options;

  // Initialize Clarity first
  initializeClarity();

  // Wait a bit for Clarity to load, then set data
  setTimeout(() => {
    // Set accommodation info
    if (accommodationName) {
      setClarityData("AccommodationName", accommodationName);
    }
    if (accommodationId) {
      setClarityData("hotelid", accommodationId);
    }

    // Set source if from Hotel Ads
    if (isFromHotelAds()) {
      setClarityData("Source", "Google Hotel Ads");
    }

    // Set availability info if provided
    if (checkIn && checkOut) {
      const availabilityInfo = `CheckIn: ${checkIn}; Checkout: ${checkOut}; Adult:${adults || 1}; Child:${children || 0};`;

      if (isAvailable === true) {
        setClarityData("Availability", availabilityInfo);
      } else if (isAvailable === false) {
        setClarityData("Not-availability", availabilityInfo);
      }
    }
  }, 1000); // Wait 1 second for Clarity to load
};

// ============================================
// GOOGLE TAG MANAGER INTEGRATION
// ============================================

let gtmInitialized = false;
let currentGtmContainerId = null; // Store the current GTM container ID

/**
 * Get GTM Container ID from accommodationGroupDetail API response
 * @param {Object} accommodationGroupDetail - API response from getaccommodationgroupdetail
 * @returns {string|null} GTM Container ID or null
 */
export const getGtmContainerId = (accommodationGroupDetail) => {
  if (!accommodationGroupDetail) return null;

  // Try multiple possible field names and nested structures
  const gtmCode = accommodationGroupDetail?.GtmCode ||
                  accommodationGroupDetail?.gtmCode ||
                  accommodationGroupDetail?.GTMCode ||
                  accommodationGroupDetail?.gtm_code ||
                  accommodationGroupDetail?.hotelMapping?.GtmCode ||
                  accommodationGroupDetail?.hotelMapping?.gtmCode ||
                  accommodationGroupDetail?.accommodationDetails?.[0]?.GtmCode ||
                  accommodationGroupDetail?.accommodationdetailsdb?.[0]?.GtmCode ||
                  null;

  return gtmCode || null;
};

/**
 * Initialize Google Tag Manager
 * @param {string} containerId - GTM container ID (required - from accommodationGroupDetail.GtmCode)
 */
export const initializeGTM = (containerId) => {
  if (!containerId || typeof window === 'undefined') {
    return;
  }

  // If already initialized with same container ID, skip
  if (gtmInitialized && currentGtmContainerId === containerId) {
    return;
  }

  try {
    // Check if already initialized with different container
    if (window.google_tag_manager && window.google_tag_manager[containerId]) {
      gtmInitialized = true;
      currentGtmContainerId = containerId;
      return;
    }

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];

    // GTM initialization script
    (function(w, d, s, l, i) {
      w[l] = w[l] || [];
      w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      var f = d.getElementsByTagName(s)[0],
          j = d.createElement(s),
          dl = l != 'dataLayer' ? '&l=' + l : '';
      j.async = true;
      j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
      f.parentNode.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', containerId);

    gtmInitialized = true;
    currentGtmContainerId = containerId;
    console.log('GTM: Initialized with container ID:', containerId);
  } catch (error) {
    console.error('GTM: Failed to initialize:', error);
  }
};

/**
 * Push event to GTM dataLayer
 * @param {Object} eventData - Event data to push
 */
export const pushToDataLayer = (eventData) => {
  try {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push(eventData);
      console.log('GTM: Pushed to dataLayer:', eventData);
    }
  } catch (error) {
    console.error('GTM: Failed to push to dataLayer:', error);
  }
};

// ============================================
// GOOGLE ANALYTICS (gtag.js) INTEGRATION
// ============================================

let gtagInitialized = false;

/**
 * Get tracking codes from accommodationGroupDetail API response
 * Fields: AnalyticsCode, AWConversionCode, FacebookPixelId, GtmCode
 * @param {Object} accommodationGroupDetail - API response
 * @returns {Object} { analyticsCode, awConversionCode, facebookPixelId, gtmCode }
 */
export const getTrackingCodes = (accommodationGroupDetail) => {
  if (!accommodationGroupDetail) return {};

  const details = accommodationGroupDetail?.accommodationdetailsdb?.[0] ||
                  accommodationGroupDetail?.accommodationDetails?.[0] || {};

  return {
    analyticsCode: details.AnalyticsCode || details.analyticsCode || accommodationGroupDetail?.AnalyticsCode || null,
    awConversionCode: details.AWConversionCode || details.awConversionCode || accommodationGroupDetail?.AWConversionCode || null,
    conversionCode: details.ConversionCode || details.conversionCode || accommodationGroupDetail?.ConversionCode || null,
    facebookPixelId: details.FacebookPixelId || details.facebookPixelId || accommodationGroupDetail?.FacebookPixelId || null,
    gtmCode: details.GtmCode || details.gtmCode || accommodationGroupDetail?.GtmCode || getGtmContainerId(accommodationGroupDetail)
  };
};

/**
 * Initialize Google Analytics (gtag.js)
 * If AnalyticsCode exists: load gtag with AnalyticsCode, add AWConversionCode if HotelAds
 * If no AnalyticsCode but AWConversionCode exists: load gtag with AWConversionCode
 * @param {string} analyticsCode - GA Measurement ID (e.g., G-XXXXXXX or UA-XXXXXXX)
 * @param {string} awConversionCode - Google Ads Conversion ID (e.g., AW-XXXXXXX)
 */
export const initializeGoogleAnalytics = (analyticsCode, awConversionCode) => {
  if (typeof window === 'undefined' || gtagInitialized) return;
  if (!analyticsCode && !awConversionCode) return;

  try {
    const primaryId = analyticsCode || awConversionCode;

    // Load gtag.js script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${primaryId}`;
    document.head.appendChild(script);

    // Initialize dataLayer and gtag function
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());

    if (analyticsCode) {
      // If HotelAds campaign, also config AWConversionCode
      if (isFromHotelAds() && awConversionCode) {
        window.gtag('config', awConversionCode);
      }
      window.gtag('config', analyticsCode);
      console.log('Google Analytics: Initialized with', analyticsCode);
    } else {
      // No AnalyticsCode, use AWConversionCode only
      window.gtag('config', awConversionCode);
      console.log('Google Analytics: Initialized with AWConversionCode', awConversionCode);
    }

    gtagInitialized = true;
  } catch (error) {
    console.error('Google Analytics: Failed to initialize:', error);
  }
};

// ============================================
// FACEBOOK PIXEL INTEGRATION
// ============================================

let fbPixelInitialized = false;

/**
 * Initialize Facebook Pixel
 * @param {string} pixelId - Facebook Pixel ID
 */
export const initializeFacebookPixel = (pixelId) => {
  if (!pixelId || typeof window === 'undefined' || fbPixelInitialized) return;

  try {
    // Check if already initialized
    if (window.fbq) {
      fbPixelInitialized = true;
      return;
    }

    // Facebook Pixel initialization script
    (function(f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function() {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = true;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

    window.fbq('init', pixelId);
    window.fbq('track', 'PageView');

    // Add noscript fallback image
    const noscriptImg = document.createElement('img');
    noscriptImg.height = 1;
    noscriptImg.width = 1;
    noscriptImg.style.display = 'none';
    noscriptImg.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
    document.body.appendChild(noscriptImg);

    fbPixelInitialized = true;
    console.log('Facebook Pixel: Initialized with ID:', pixelId);
  } catch (error) {
    console.error('Facebook Pixel: Failed to initialize:', error);
  }
};

// ============================================
// GTM NOSCRIPT IFRAME
// ============================================

let gtmNoscriptAdded = false;

/**
 * Add GTM noscript iframe to document body
 * @param {string} containerId - GTM container ID
 */
export const addGtmNoscriptIframe = (containerId) => {
  if (!containerId || typeof document === 'undefined' || gtmNoscriptAdded) return;

  try {
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);

    // Insert at the beginning of body
    if (document.body.firstChild) {
      document.body.insertBefore(noscript, document.body.firstChild);
    } else {
      document.body.appendChild(noscript);
    }

    gtmNoscriptAdded = true;
    console.log('GTM: Added noscript iframe for container:', containerId);
  } catch (error) {
    console.error('GTM: Failed to add noscript iframe:', error);
  }
};

/**
 * Fire Google Ads conversion event on booking confirmation page
 * Only fires when session has HotelAds (utm_campaign=HotelAds)
 * Equivalent to Blade: gtag('event', 'conversion', { send_to, value, currency, transaction_id })
 * @param {Object} options
 * @param {string} options.conversionCode - ConversionCode from accommodationdetailsdb
 * @param {number} options.totalPrice - Total booking price
 * @param {string} options.currency - Currency code (e.g., 'INR')
 * @param {string} options.bookingId - Booking/transaction ID
 */
export const fireGoogleAdsConversion = ({ conversionCode, totalPrice, currency, bookingId }) => {
  if (!isFromHotelAds() || !conversionCode) return;
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

  try {
    window.gtag('event', 'conversion', {
      'send_to': conversionCode,
      'value': totalPrice,
      'currency': currency,
      'transaction_id': bookingId
    });
    console.log('Google Ads: Conversion fired', { conversionCode, totalPrice, currency, bookingId });
  } catch (error) {
    console.error('Google Ads: Failed to fire conversion:', error);
  }
};

// ============================================
// GOOGLE ANALYTICS MEASUREMENT PROTOCOL (Universal Analytics)
// Transaction & Item tracking via collect endpoint
// ============================================

/**
 * Send transaction data to Google Analytics via Measurement Protocol
 * Equivalent to PHP TransactionAnalytic function
 * @param {Object} options
 * @param {string} options.analyticsCode - GA Tracking ID (e.g., UA-XXXXXXX)
 * @param {string} options.clientId - Client ID (from _ga param / wcid)
 * @param {string} options.bookingId - Booking/Transaction ID
 * @param {string} options.hotelName - Hotel/Accommodation name
 * @param {number} options.totalPrice - Total transaction price
 * @param {string} options.currencyCode - Currency code (e.g., 'PKR', 'USD')
 */
export const sendTransactionAnalytic = ({ analyticsCode, clientId, bookingId, hotelName, totalPrice, currencyCode }) => {
  if (!analyticsCode || !clientId) return;

  try {
    const params = new URLSearchParams({
      v: '1',
      tid: analyticsCode,
      cid: clientId,
      t: 'transaction',
      ti: bookingId,
      ta: hotelName,
      tr: totalPrice,
      ts: '0.00',
      tt: '0.00',
      cd1: '52ea5a8bc6a4a',
      cu: currencyCode
    });

    // Use sendBeacon for reliability, fallback to fetch
    const url = 'https://www.google-analytics.com/collect';
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, params.toString());
    } else {
      fetch(url, {
        method: 'POST',
        body: params.toString(),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        keepalive: true
      });
    }

    console.log('GA Measurement Protocol: Transaction sent', { bookingId, hotelName, totalPrice, currencyCode });
  } catch (error) {
    console.error('GA Measurement Protocol: Failed to send transaction:', error);
  }
};

/**
 * Send item (room) data to Google Analytics via Measurement Protocol
 * Equivalent to PHP ItemAddAnalytic function
 * @param {Object} options
 * @param {string} options.analyticsCode - GA Tracking ID (e.g., UA-XXXXXXX)
 * @param {string} options.clientId - Client ID (from _ga param / wcid)
 * @param {string} options.bookingId - Booking/Transaction ID
 * @param {string} options.roomName - Room name
 * @param {number} options.roomPrice - Room price
 * @param {string} options.ratePlanId - Rate Plan ID
 * @param {string} options.ratePlanName - Rate Plan name
 * @param {number} options.roomQuantity - Room quantity
 * @param {string} options.currencyCode - Currency code
 */
export const sendItemAnalytic = ({ analyticsCode, clientId, bookingId, roomName, roomPrice, ratePlanId, ratePlanName, roomQuantity, currencyCode }) => {
  if (!analyticsCode || !clientId) return;

  try {
    const params = new URLSearchParams({
      v: '1',
      tid: analyticsCode,
      cid: clientId,
      t: 'item',
      ti: bookingId,
      in: roomName,
      ip: roomPrice,
      ic: ratePlanId,
      iv: ratePlanName,
      iq: roomQuantity,
      cd1: '52ea5a8bc6a4a',
      cu: currencyCode
    });

    const url = 'https://www.google-analytics.com/collect';
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, params.toString());
    } else {
      fetch(url, {
        method: 'POST',
        body: params.toString(),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        keepalive: true
      });
    }

    console.log('GA Measurement Protocol: Item sent', { bookingId, roomName, roomPrice, ratePlanName });
  } catch (error) {
    console.error('GA Measurement Protocol: Failed to send item:', error);
  }
};

/**
 * Fire full transaction tracking (transaction + all room items)
 * Combines TransactionAnalytic + ItemAddAnalytic for all rooms
 * Call this on ConfirmationPage when wcid/_ga param exists
 * @param {Object} options
 * @param {string} options.analyticsCode - GA Tracking ID
 * @param {string} options.clientId - Client ID (wcid / _ga param)
 * @param {string} options.bookingId - Booking ID
 * @param {string} options.hotelName - Hotel name
 * @param {number} options.totalPrice - Total price
 * @param {string} options.currencyCode - Currency code
 * @param {Array} options.rooms - Array of room objects { roomName, roomPrice, ratePlanId, ratePlanName, roomQuantity }
 */
export const fireTransactionTracking = ({ analyticsCode, clientId, bookingId, hotelName, totalPrice, currencyCode, rooms = [] }) => {
  if (!analyticsCode || !clientId) {
    console.log('GA Measurement Protocol: Skipped - no analyticsCode or clientId');
    return;
  }

  // Send transaction hit
  sendTransactionAnalytic({ analyticsCode, clientId, bookingId, hotelName, totalPrice, currencyCode });

  // Send item hit for each room
  rooms.forEach((room) => {
    sendItemAnalytic({
      analyticsCode,
      clientId,
      bookingId,
      roomName: room.roomName || room.RoomName,
      roomPrice: room.roomPrice || room.TotalRate || room.totalRate,
      ratePlanId: room.ratePlanId || room.RatePlanID || room.RoomID || '',
      ratePlanName: room.ratePlanName || room.RatePlanName || '',
      roomQuantity: room.roomQuantity || room.RoomQuantity || 1,
      currencyCode
    });
  });
};

/**
 * Initialize all tracking (Clarity + GTM + Google Analytics + Facebook Pixel) with accommodation data
 * Use this on pages where tracking should be enabled
 * Call from DesktopComponent, AvailabilityComponent, UnifiedCheckout
 * @param {Object} options - Tracking options
 * @param {Object} options.accommodationGroupDetail - API response for all tracking codes
 */
// ============================================
// SESSION ID & BOOKING TRACKING API
// ============================================

const SESSION_ID_KEY = 'booking_session_id';

/**
 * Generate a unique session ID
 * @returns {string} Unique session ID
 */
const generateSessionId = () => {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 15);
  return `${timestamp}-${randomPart}`;
};

/**
 * Get or create session ID
 * Priority: 1) URL param 2) sessionStorage 3) generate new
 * Once found/created, stores in both sessionStorage and URL
 * @returns {string} Session ID
 */
export const getSessionId = () => {
  try {
    // 1) Check URL params first (persists across page navigations)
    const urlParams = new URLSearchParams(window.location.search);
    const urlSessionId = urlParams.get('sessionId');

    // 2) Check sessionStorage
    const storedSessionId = sessionStorage.getItem(SESSION_ID_KEY);

    // Use URL param > sessionStorage > generate new
    let sessionId = urlSessionId || storedSessionId;

    if (!sessionId) {
      sessionId = generateSessionId();
    }

    // Always save to sessionStorage for persistence
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);

    return sessionId;
  } catch (error) {
    return generateSessionId();
  }
};

/**
 * Destroy session ID (call on confirmation/3rd page)
 * Next visit to first page will generate a new session ID
 */
export const destroySessionId = () => {
  try {
    sessionStorage.removeItem(SESSION_ID_KEY);
  } catch (error) {
    // silently fail
  }
};

/**
 * Force generate a new session ID (call on first page load)
 * Always creates fresh session, ignoring URL/sessionStorage
 * @returns {string} New session ID
 */
export const resetSessionId = () => {
  const sessionId = generateSessionId();
  try {
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  } catch (error) {
    // silently fail
  }
  return sessionId;
};

/**
 * Get browser name from user agent
 * @returns {string} Browser name
 */
const getBrowserName = () => {
  const userAgent = navigator.userAgent;
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) return 'Safari';
  if (userAgent.includes('Edg')) return 'Edge';
  if (userAgent.includes('Opera') || userAgent.includes('OPR')) return 'Opera';
  return 'Unknown';
};

/**
 * Get device type
 * @returns {string} 'mobile' or 'desktop'
 */
const getDeviceType = () => {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  return isMobile ? 'm' : 'd';
};

/**
 * Call booking tracking API
 * @param {Object} options
 * @param {number} options.hotel_id - Hotel ID
 * @param {string} options.checkin - Check-in date
 * @param {string} options.checkout - Check-out date
 * @param {string} options.domain - Domain URL
 * @param {string} options.wcid - WCID parameter
 * @param {number} options.step - Current step (1 = checkout page)
 * @param {string} options.lang - Language code
 * @param {Object} options.pageData - Additional page data (room details etc.)
 */
export const sendBookingTracking = async (options) => {
  try {
    const sessionId = getSessionId();

    const payload = {
      hotel_id: options.hotel_id,
      checkin: options.checkin,
      checkout: options.checkout,
      domain: options.domain,
      sessionId: sessionId,
      device: getDeviceType(),
      url: window.location.href,
      wcid: options.wcid || '',
      step: options.step || 1,
      lang: options.lang || 'en',
      browser: getBrowserName(),
      ipaddress: '', // Will be filled by server
      pageData: options.pageData || {}
    };

    const url = 'https://securereservation.org/hotels/api/bookingTracking';

    // Log the data being sent
    console.log('=== BOOKING TRACKING DATA ===');
    console.log('Payload:', payload);
    console.log('=============================');

    // Field names matching Laravel controller: $request->hotel_id, $request->checkin, etc.
    const trackingData = {
      hotel_id: payload.hotel_id || '',
      checkin: payload.checkin || '',
      checkout: payload.checkout || '',
      domain: payload.domain || '',
      sessionId: payload.sessionId || '',
      device: payload.device || '',
      url: payload.url || '',
      wcid: payload.wcid || '',
      step: String(payload.step || 1),
      lang: payload.lang || 'en',
      browser: payload.browser || '',
      ipaddress: '',
      pageData: payload.pageData || {}
    };

    console.log('Tracking Data:', trackingData);

    // Hidden iframe form POST (normal POST like step 1 & 2)
    const iframeName = 'tracking_iframe_' + Date.now();
    const iframe = document.createElement('iframe');
    iframe.name = iframeName;
    iframe.style.display = 'none';
    document.body.appendChild(iframe);

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = url;
    form.target = iframeName;
    form.style.display = 'none';

    const fields = {
      hotel_id: trackingData.hotel_id,
      checkin: trackingData.checkin,
      checkout: trackingData.checkout,
      domain: trackingData.domain,
      sessionId: trackingData.sessionId,
      device: trackingData.device,
      url: trackingData.url,
      wcid: trackingData.wcid,
      step: trackingData.step,
      lang: trackingData.lang,
      browser: trackingData.browser,
      ipaddress: trackingData.ipaddress,
      pageData: JSON.stringify(trackingData.pageData)
    };

    Object.entries(fields).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    console.log('Booking Tracking: Sent via form POST, step:', trackingData.step);

    // Cleanup after 5 seconds
    setTimeout(() => {
      if (form.parentNode) form.parentNode.removeChild(form);
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }, 5000);

    return sessionId;
  } catch (error) {
    console.error('Booking Tracking: Failed to send data:', error);
    return getSessionId();
  }
};

/**
 * Add session ID to URL search params
 * @param {URLSearchParams} searchParams - URL search params
 * @returns {URLSearchParams} Updated params with session ID
 */
export const addSessionIdToParams = (searchParams) => {
  const sessionId = getSessionId();
  searchParams.set('sessionId', sessionId);
  return searchParams;
};

export const initializeAllTracking = (options = {}) => {
  // Initialize Clarity with data
  initializeClarityWithData(options);

  // Get all tracking codes from accommodationGroupDetail
  const trackingCodes = getTrackingCodes(options.accommodationGroupDetail);

  // Initialize GTM if container ID is available
  const gtmContainerId = options.gtmContainerId || trackingCodes.gtmCode;
  if (gtmContainerId) {
    initializeGTM(gtmContainerId);
    addGtmNoscriptIframe(gtmContainerId);

    // Push initial data to dataLayer
    pushToDataLayer({
      event: 'pageView',
      accommodationName: options.accommodationName || '',
      accommodationId: options.accommodationId || '',
      source: isFromHotelAds() ? 'Google Hotel Ads' : 'Direct'
    });
  }

  // Initialize Google Analytics (AnalyticsCode / AWConversionCode)
  initializeGoogleAnalytics(trackingCodes.analyticsCode, trackingCodes.awConversionCode);

  // Initialize Facebook Pixel
  if (trackingCodes.facebookPixelId) {
    initializeFacebookPixel(trackingCodes.facebookPixelId);
  }
};
